<template>
    <form
        ref="form-element"
        novalidate
        class="booking-dialog__form"
        :aria-busy="isSubmitting || undefined"
        @submit.prevent="submitForm"
        @focusin.once="emit('started')"
    >
        <label
            class="k-visually-hidden"
            aria-hidden="true"
        >
            Website
            <input
                v-model="website"
                name="website"
                tabindex="-1"
                autocomplete="off"
            >
        </label>
        <form.Field
            v-slot="{ field }"
            name="fullname"
            :validators="{
                onDynamic: ({ value }) => requiredText(value, 'Enter your full name.'),
            }"
        >
            <KField
                label="Full name"
                hide-label
                required
                :error="firstError(field.state.meta.errors)"
            >
                <KInput
                    name="fullname"
                    autocomplete="name"
                    placeholder="Full name"
                    required
                    :model-value="field.state.value"
                    @blur="field.handleBlur"
                    @update:model-value="value => field.handleChange(String(value))"
                />
            </KField>
        </form.Field>

        <form.Field
            v-slot="{ field }"
            name="email"
            :validators="{ onDynamic: ({ value }) => validEmail(value) }"
        >
            <KField
                label="Email"
                hide-label
                required
                :error="firstError(field.state.meta.errors)"
            >
                <KInput
                    type="email"
                    name="email"
                    autocomplete="email"
                    placeholder="Your e-mail"
                    required
                    :model-value="field.state.value"
                    @blur="field.handleBlur"
                    @update:model-value="value => field.handleChange(String(value))"
                />
            </KField>
        </form.Field>

        <form.Field
            v-slot="{ field }"
            name="phone"
            :validators="{
                onDynamic: ({ value }) => requiredText(value, 'Enter your phone number.'),
            }"
        >
            <KField
                label="Phone number"
                hide-label
                required
                :error="firstError(field.state.meta.errors)"
            >
                <KInput
                    type="tel"
                    name="phone"
                    autocomplete="tel"
                    placeholder="Phone number"
                    required
                    :model-value="field.state.value"
                    @blur="field.handleBlur"
                    @update:model-value="value => field.handleChange(String(value))"
                />
            </KField>
        </form.Field>

        <form.Field
            v-slot="{ field }"
            name="message"
        >
            <KField
                label="Anything else we should know?"
                hide-label
            >
                <KTextarea
                    name="message"
                    :rows="3"
                    placeholder="Anything else we should know? (optional)"
                    :model-value="field.state.value"
                    @blur="field.handleBlur"
                    @update:model-value="field.handleChange"
                />
            </KField>
        </form.Field>

        <form.Field
            v-slot="{ field }"
            name="consent"
            :validators="{
                onDynamic: ({ value }) => value
                    ? undefined
                    : 'Agree to the Privacy Policy to continue.',
            }"
        >
            <KField
                required
                :error="firstError(field.state.meta.errors)"
            >
                <KCheckbox
                    required
                    :model-value="field.state.value"
                    @blur="field.handleBlur"
                    @update:model-value="value => field.handleChange(value === true)"
                >
                    <template #default>
                        I agree to the
                        <a
                            href="/privacy-policy"
                            target="_blank"
                        >Privacy Policy</a>.
                    </template>
                </KCheckbox>
            </KField>
        </form.Field>

        <KButton
            type="submit"
            full-width
            :disabled="isSubmitting"
        >
            {{ isSubmitting ? 'Sending...' : 'Send booking request' }}
        </KButton>

        <p
            v-if="serverError"
            class="booking-dialog__error"
            role="alert"
        >
            Something went wrong — please try again or book via WhatsApp.
        </p>
    </form>
</template>

<script setup lang="ts">
import type { RequestSubmission } from '@vemedia/request-contract'
import type { BookingSummary } from '~/utils/forms'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { KButton, KCheckbox, KField, KInput, KTextarea } from '@vemedia/karibu'
import { nextTick, ref, useTemplateRef } from 'vue'
import {
    buildBookingRequest,
    EMPTY_BOOKING_FORM,
    requiredText,
    validEmail,
} from '~/utils/forms'

const { booking, send } = defineProps<{
    booking: BookingSummary
    send: (payload: RequestSubmission) => Promise<void>
}>()

const emit = defineEmits<{
    started: []
    submitted: [payload: RequestSubmission]
    submitError: []
    invalid: []
}>()

const formElement = useTemplateRef<HTMLFormElement>('form-element')
const serverError = ref(false)
const submitted = ref(false)
const website = ref('')

const form = useForm({
    defaultValues: { ...EMPTY_BOOKING_FORM },
    validationLogic: revalidateLogic({
        mode: 'blur',
        modeAfterSubmission: 'change',
    }),
    onSubmitInvalid: () => {
        emit('invalid')
        void nextTick(() => {
            formElement.value
                ?.querySelector<HTMLElement>('[aria-invalid="true"]')
                ?.focus()
        })
    },
    onSubmit: async ({ value }) => {
        const payload = buildBookingRequest(value, booking, website.value)

        await send(payload)
        submitted.value = true
        emit('submitted', payload)
    },
})

const isSubmitting = form.useSelector(state => state.isSubmitting)

function firstError(errors: unknown[]) {
    return errors.find(error => typeof error === 'string') as string | undefined
}

async function submitForm() {
    // TanStack 1.33.2 does not deduplicate direct `handleSubmit()` calls, and a finished
    // booking must not be resent; server errors leave the form retryable.
    if (isSubmitting.value || submitted.value) {
        return
    }

    serverError.value = false

    try {
        await form.handleSubmit()
    } catch {
        serverError.value = true
        emit('submitError')
    }
}
</script>
